import { majorToMinor } from '../../../../currency'
import type {
  OrgRegistration,
  OrgRegistrationApi,
  OrgRegistrationDivision,
  OrgRegistrationDivisionApi,
  OrgRegistrationDivisionGenderApi,
  OrgRegistrationDivisionOption,
} from '../types'
import { toOrgRegistrationVenues } from './venues'

/** The gender a division carries until the organizer picks one. */
const UNSET_GENDER_ID = '4'

const toGenderLabel = (
  gender: OrgRegistrationDivisionGenderApi | null | undefined
): string => (!gender || gender.id === UNSET_GENDER_ID ? '' : gender.name ?? '')

/** Dollars → cents; a missing fee means no payment is required. */
const toMinor = (amount: number | null | undefined, currency: string): number =>
  majorToMinor(amount ?? 0, currency)

const toDeposit = (
  amount: number | null | undefined,
  currency: string
): number | null => (amount ? majorToMinor(amount, currency) : null)

const toPlayerOption = (
  raw: OrgRegistrationDivisionApi,
  currency: string
): OrgRegistrationDivisionOption | null => {
  if (!raw.is_player_registration) {
    return null
  }
  // The backend counts player spots against every team's full roster.
  const capacity =
    raw.max_teams !== null && raw.max_players_per_team !== null
      ? raw.max_teams * raw.max_players_per_team
      : null
  return {
    fee: toMinor(raw.player_cost, currency),
    deposit: toDeposit(raw.player_deposit, currency),
    capacity,
    spotsLeft: raw.player_spots_left ?? null,
  }
}

const toTeamOption = (
  raw: OrgRegistrationDivisionApi,
  currency: string
): OrgRegistrationDivisionOption | null =>
  raw.is_team_registration
    ? {
        fee: toMinor(raw.team_cost, currency),
        deposit: toDeposit(raw.deposit, currency),
        capacity: raw.max_teams ?? null,
        spotsLeft: raw.team_spots_left ?? null,
      }
    : null

const toDivision = (
  raw: OrgRegistrationDivisionApi
): OrgRegistrationDivision => {
  const currency = (raw.currency_code || '').toUpperCase()
  return {
    id: raw.id,
    name: raw.name,
    ageLabel: raw.division_age_v2 ?? '',
    genderLabel: toGenderLabel(raw.division_gender),
    currency,
    player: toPlayerOption(raw, currency),
    team: toTeamOption(raw, currency),
  }
}

/** One API registration → the shape the list renders. */
export const toOrgRegistration = (
  raw: OrgRegistrationApi
): OrgRegistration => ({
  id: raw.id,
  scheduleId: raw.schedule_id,
  name: raw.name,
  image: raw.image,
  organizationId: raw.organization.id,
  organizationName: raw.organization.name_full,
  organizationImage: raw.organization.image,
  sport: raw.sport,
  genericSport: raw.generic_sport_properties ?? null,
  status: raw.status,
  opensAt: raw.starts_at ?? null,
  closesAt: raw.ends_at ?? null,
  seasonStartsAt: raw.schedule_starts_at ?? null,
  seasonEndsAt: raw.schedule_ends_at ?? null,
  timezone: raw.timezone,
  divisions: (raw.divisions ?? []).map(toDivision),
  venues: toOrgRegistrationVenues(raw.venues),
})

/** A response's `data`, tolerating a missing or malformed list. */
export const toOrgRegistrations = (
  raw: OrgRegistrationApi[] | null | undefined
): OrgRegistration[] => (Array.isArray(raw) ? raw.map(toOrgRegistration) : [])
